import debounce from 'lodash-es/debounce';
import { DirectiveBinding, nextTick } from 'vue';

import calculateElementOverflow from '../../utils/calculateElementOverflow';

export enum Positions {
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right',
  Top = 'top',
}

type HTMLDirectiveElement = HTMLElement & { observer?: ResizeObserver; cachedVal: any };

const defaultPosition = Positions.Top;
const positions = Object.values(Positions);

/**
 * Gets position name based on modifiers provided. Defaults to Top.
 */
function getPosition(modifiers: DirectiveBinding['modifiers']): Positions {
  let position = defaultPosition;

  positions.forEach((pos) => {
    if (modifiers[pos]) {
      position = pos;
      return false;
    }
  });

  return position;
}

/**
 * Appends a new tooltip element to the directive element.
 *
 * Adds .tooltip-toggle class to element.
 *
 */
function mount(el: HTMLDirectiveElement, binding: DirectiveBinding) {
  if (binding.value) {
    const tooltip = document.createElement('div');

    const styles = `tooltip tooltip--${getPosition(binding.modifiers)}`;

    tooltip.classList.value = styles;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.innerHTML = binding.value;

    el.appendChild(tooltip);

    // Add classname to wrapper element for styling
    el.classList.add('tooltip-toggle');
    // Apply tabindex to make focusable (if it doesn't already have)
    el.tabIndex = 0;

    nextTick().then(() => {
      if (styles.includes('bottom') || styles.includes('top')) {
        const overflow = calculateElementOverflow(tooltip);

        if (overflow?.value) {
          const symbol = overflow.side === 'right' ? '-' : '';

          tooltip.style.setProperty('--offset', `${symbol}${overflow.value}`);
        }
      }
    });
  }
}

/**
 * Removes tooltip & cleans up any classnames added
 */
function unmount(el: HTMLElement) {
  const tooltip = el.querySelector('.tooltip');

  el.classList.remove('tooltip-toggle');
  el.style.removeProperty('--offset');

  if (tooltip) {
    tooltip.remove();
  }
}

/**
 * Remounts tooltip to re-calc placement
 */
function remount(el: HTMLDirectiveElement, binding: DirectiveBinding) {
  unmount(el);
  mount(el, binding);

  // If the value has not changed, avoid re-initializing observer
  // This prevents a `remount` infinite loop
  if (el.cachedVal !== binding.value) {
    el.cachedVal = binding.value;
    destroyObserver(el);
    setupObserver(el, binding);
  }
}

/**
 * Initializes a ResizeObserver for the current element to remount on resize
 */
function setupObserver(el: HTMLDirectiveElement, binding: DirectiveBinding) {
  /**
   * Cache value on element because when we setup observer
   * it's still referencing the original value from on `beforeMount`.
   * When the directive updates with a new value, we'll verify
   * the new value is different from the cached one during remount.
   */
  el.cachedVal = binding.value;

  if ('ResizeObserver' in window && 'body' in document) {
    // debounce to limit remounts to after resize
    el.observer = new ResizeObserver(
      debounce(() => {
        remount(el, binding);
      }, 300),
    );

    el.observer.observe(document.body);
  }
}

/**
 * Removes the current observer
 */
function destroyObserver(el: HTMLDirectiveElement) {
  if (el.observer && document.body) {
    el.observer.unobserve(document.body);
    el.observer.disconnect();
    el.observer = undefined;
  }
}

/**
 * Tooltip directive
 * @deprecated Use the `Tooltip` component from `@leaflink/stash/Tooltip.vue` instead
 */
export default {
  beforeMount(el: HTMLDirectiveElement, binding: DirectiveBinding) {
    mount(el, binding);
    setupObserver(el, binding);
  },

  updated(el: HTMLDirectiveElement, binding: DirectiveBinding) {
    const { oldValue, value } = binding;

    if (oldValue !== value) {
      remount(el, binding);
    }
  },

  unmounted(el: HTMLDirectiveElement) {
    destroyObserver(el);
    unmount(el);
  },
};
